import React, { useCallback, useEffect, useState, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import ru from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import '../../styles/DatePicker.css';


import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Tooltip,
    Select,
    MenuItem,
    Chip,
    Button,
    IconButton,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    TextField,
    FormControl,
    InputLabel,
    CircularProgress,
    Autocomplete,
    Checkbox,
    ListItemText,
    Menu,
    Divider,
    TableSortLabel,
    ImageList,
    ImageListItem,
    ImageListItemBar,
    Dialog,
    DialogContent,
    DialogTitle,
} from '@mui/material';
import {
    Edit as EditIcon,
    Add as AddIcon,
    Business as BusinessIcon,
    Search as SearchIcon,
    FilterList as FilterIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Delete as DeleteIcon,
    History as HistoryIcon,
    Clear as ClearIcon,
    Save as SaveIcon,
    LocationOn as LocationOnIcon,
    FileDownload as FileDownloadIcon,
    PictureAsPdf as PictureAsPdfIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    UnfoldMore as UnfoldMoreIcon,
    ViewColumn as ViewColumnIcon,
    PlayArrow as PlayIcon,
    Close as CloseIcon,
    VideoLibrary as VideoIcon
} from '@mui/icons-material';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { GroupedTableVirtuoso } from 'react-virtuoso';
import { useSelector, useDispatch } from 'react-redux';
import { toggleDarkMode } from '../../store/reducers/stateReducer';
import { $authHost } from '../../http/index.js';
import { ktpIcon, pillarIcon, projectStatusOreolIcon } from '../layers/icons.js';
import '../../utils/arial-normal.js'
import '../../utils/arial-bold.js'
import Viewer from 'react-viewer';
import { useAuth } from 'react-oidc-context';
import { AdvancedFiltersComponent } from './common/AdvancedFilters.jsx';
// Регистрируем русскую локаль
registerLocale('ru', ru);


function formatRuble(str) {
    return new Intl.NumberFormat('ru-RU', {
        style: 'currency',
        currency: 'RUB',
    }).format(str || 0)
}

function formatDateShort(str) {
    return formatDate(str, true)
}

function formatDate(str, short = false) {
    if (!str) return '';

    // Если дата в формате "YYYY-MM-DD" (из базы данных)
    if (str.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [, year, month, day] = str.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата уже в формате "DD-MM-YYYY"
    if (str.match(/^\d{2}-\d{2}-\d{4}$/)) {
        const [, day, month, year] = str.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        return `${day}.${month}.${year}`;
    }

    // Если дата в формате "HH:MM DD-MM-YYYY"
    if (str.match(/^\d{2}:\d{2}\s\d{2}-\d{2}-\d{4}$/)) {
        const [, hours, minutes, day, month, year] = str.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        return `${hours}:${minutes} ${day}.${month}.${year}`;
    }

    // Попытка парсинга как обычной даты
    try {
        const date = new Date(str);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        return short ? `${day}.${month}.${year}` : `${hours}:${minutes} ${day}.${month}.${year}`;
    } catch {
        return '';
    }
}

const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    try {
        // Если дата уже в формате "YYYY-MM-DD" (из базы данных)
        if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return dateString;
        }

        // Если дата в формате "HH:MM DD-MM-YYYY"
        const dateTimeMatch = dateString.match(/^(\d{2}):(\d{2})\s(\d{2})-(\d{2})-(\d{4})$/);
        if (dateTimeMatch) {
            const [, hours, minutes, day, month, year] = dateTimeMatch;
            return `${year}-${month}-${day}`;
        }

        // Если дата в формате "DD-MM-YYYY"  
        const dateMatch = dateString.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (dateMatch) {
            const [, day, month, year] = dateMatch;
            return `${year}-${month}-${day}`;
        }

        // Попытка парсинга как обычной даты
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

// Функция для форматирования даты из input в формат "YYYY-MM-DD" для базы данных
const formatDateFromInput = (inputValue) => {
    if (!inputValue) return '';
    try {
        const date = new Date(inputValue);
        if (isNaN(date.getTime())) return '';

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    } catch {
        return '';
    }
};

const columns = [
    {
        id: 'id', label: 'ID', minWidth: 60, width: 60, // Немного больше для номеров
        align: 'center',
    },
    {
        id: 'user_name', label: 'Имя пользователя', minWidth: 150, width: 250, // Больше места для имен
        align: 'center', // Лучше выравнивание для текста
    },
    {
        id: 'action', label: 'Действие', minWidth: 60, width: 60, // Немного больше для действий
        align: 'center',
    },
    {
        id: 'transformer', label: 'Трансформатор', minWidth: 60, width: 60, // Немного больше для названий
        align: 'center',
    },
    {
        id: 'pillar', label: 'Опора', minWidth: 60, width: 60, // Немного больше для названий
        align: 'center',
    },
    {
        id: 'dt', label: 'Дата', minWidth: 120, width: 120, // Больше для дат
        align: 'center',
    },
    {
        id: 'images', label: 'Фотографии', minWidth: 250, width: 250, // Больше для дат и имен
        align: 'center',
    },
    {
        id: 'videos', label: 'Видео', minWidth: 100, width: 120, // Больше для дат и имен
        align: 'center',
    },
];

const VirtuosoTableComponents = {

    Scroller: React.forwardRef((props, ref) => (
        <TableContainer component={Paper} {...props} ref={ref} sx={{
            bgcolor: 'transparent',
            boxShadow: 'none',
            // overflowX: 'auto', // для скролла
        }} />
    )),
    Table: (props) => (
        <Table {...props} sx={{
            borderCollapse: 'separate',
            tableLayout: 'auto', // Автоматическое распределение ширины
            bgcolor: 'transparent',
            width: '100%', // Используем всю доступную ширину
            minWidth: '100%', // Минимальная ширина 100%
        }} />
    ),
    TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
    )),
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
};


function fixedHeaderContent(darkMode, sortBy, sortDirection, handleSort, visibleColumns, columnWidths) {
    // Функция для получения иконки сортировки
    const getSortIcon = (columnId) => {
        if (sortBy !== columnId) {
            return null
            // return <UnfoldMoreIcon fontSize="small" sx={{ opacity: 0.5 }} />;
        }
        return sortDirection === 'asc'
            ? <ArrowUpwardIcon fontSize="small" />
            : <ArrowDownwardIcon fontSize="small" />;
    };

    // Получаем только видимые столбцы
    const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);

    return (
        <>
            <TableRow>
                {visibleColumnsArray.map((column, index) => (
                    <TableCell
                        key={column.id}
                        variant="head"
                        align={column.align || ''}
                        style={{
                            width: columnWidths[column.id] || column.minWidth,
                            minWidth: column.minWidth,
                            maxWidth: columnWidths[column.id] || column.minWidth
                        }}
                        colSpan={column.colSpan}
                        sx={{
                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                            color: darkMode ? '#f5f7fa' : '#333',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            borderBottom: darkMode
                                ? '2px solid rgba(255, 255, 255, 0.12)'
                                : '2px solid rgba(0, 0, 0, 0.12)',
                            borderRight: darkMode
                                ? '1px solid rgba(255, 255, 255, 0.08)'
                                : '1px solid rgba(0, 0, 0, 0.08)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            p: '8px',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                            },
                            // Принудительно переопределяем цвета для всех дочерних элементов
                            '& .MuiTableSortLabel-root': {
                                color: darkMode ? '#f5f7fa' : '#333',
                            },
                            '& .MuiTableSortLabel-root.MuiTableSortLabel-active': {
                                color: darkMode ? '#64b5f6' : '#1976d2',
                            },
                            '& .MuiTableSortLabel-root:hover': {
                                color: darkMode ? '#ffffff' : '#1976d2',
                            },
                            '& .MuiTableSortLabel-icon': {
                                color: darkMode ? '#f5f7fa' : '#333',
                            },
                            '& .MuiTableSortLabel-root.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
                                color: darkMode ? '#64b5f6' : '#1976d2',
                            },
                            '& .MuiTableSortLabel-root:hover .MuiTableSortLabel-icon': {
                                color: darkMode ? '#ffffff' : '#1976d2',
                            },
                        }}
                        onClick={() => handleSort(column.id)}
                    >
                        <TableSortLabel
                            active={sortBy === column.id}
                            direction={sortBy === column.id ? sortDirection : 'asc'}
                            onClick={() => handleSort(column.id)}
                            sx={{
                                color: darkMode ? '#f5f7fa' : '#333',
                                '&:hover': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                '&.MuiTableSortLabel-active': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '& .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '&.MuiTableSortLabel-active .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&:hover .MuiTableSortLabel-icon': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                // Принудительно переопределяем цвета для всех состояний
                                '&.MuiTableSortLabel-root': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '&.MuiTableSortLabel-root.MuiTableSortLabel-active': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&.MuiTableSortLabel-root:hover': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                // Стили для иконки во всех состояниях
                                '& .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '& .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#f5f7fa' : '#333',
                                },
                                '&.MuiTableSortLabel-active .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&.MuiTableSortLabel-active .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                '&:hover .MuiTableSortLabel-iconDirectionAsc': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                '&:hover .MuiTableSortLabel-iconDirectionDesc': {
                                    color: darkMode ? '#ffffff' : '#1976d2',
                                },
                                // Принудительно переопределяем все возможные цвета
                                '& *': {
                                    color: 'inherit !important',
                                },
                            }}

                        >

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                {column.tooltip ? (
                                    <Tooltip title={column.tooltip}>
                                        <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                            {column.label}
                                        </Typography>
                                    </Tooltip>
                                ) : (
                                    <Typography variant="caption" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                                        {column.label}
                                    </Typography>
                                )}
                                {/* {getSortIcon(column.id)} */}
                            </Box>

                        </TableSortLabel>
                    </TableCell>

                ))}
            </TableRow>

        </>
    );
}

// Функция для отображения заголовка группы
function groupContent(index, groupedData, darkMode, user, visibleColumns) {
    console.log(groupedData)
    if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
        return null;
    }

    const firstItemInGroup = groupedData.groupedData.find(item => item.group === index);
    const groupName = firstItemInGroup?.groupName || `Группа ${index}`;
    const groupCount = groupedData.groupCounts[index];


    return (
        <td colSpan={columns.filter(col => visibleColumns[col.id]).reduce((a, v) => a + (v.colSpan || 1), 0)} style={{
            backgroundColor: darkMode ? '#1a1a1a' : '#f0f0f0',
            color: darkMode ? '#64b5f6' : '#1976d2',
            fontWeight: 600,
            fontSize: '0.9rem',
            padding: '6px 16px 3px 16px',
            borderBottom: darkMode
                ? '2px solid rgba(255, 255, 255, 0.12)'
                : '2px solid rgba(0, 0, 0, 0.12)',
            borderTop: darkMode
                ? '2px solid rgba(255, 255, 255, 0.12)'
                : '2px solid rgba(0, 0, 0, 0.12)',
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: 'column'
            }}>


                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2
                }}>



                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.1 }}>
                            {groupName}
                        </Typography>
                    </Box>


                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Chip с количеством объектов */}
                        <Chip
                            label={`${groupCount}`}
                            size="small"
                            onClick={() => { }}
                            sx={{
                                bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                fontSize: '0.7rem',
                                fontWeight: 600,
                                border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                            }}
                        />


                    </Box>

                </Box>


            </Box>
        </td>
    );
}


const fieldColors = {
    name: { light: '#2e7d32', dark: '#4caf50' },
    dates: { light: '#1976d2', dark: '#64b5f6' },
    stage: { light: '#6a1b9a', dark: '#ab47bc' },
    responsible: { light: '#d32f2f', dark: '#f44336' },
    coexecutors: { light: '#f57c00', dark: '#ffb74d' },
    equipment: { light: '#3949ab', dark: '#5c6bc0' },
    closing: { light: '#e65100', dark: '#ff8f00' },
    cost: { light: '#c2185b', dark: '#e91e63' },
    closing: { light: '#e65100', dark: '#ff8f00' },
    comment: { light: '#1565c0', dark: '#2196f3' },
};

const actions = {
    'start': 'Начало работ',
    'handle': 'Регистрация объекта',
    'end': 'Конец работ'
};

// Функция для открытия обзора опоры в новой вкладке
const openObjectView = (object) => {
    if (!object.coords && !object.x_y_coord) {
        console.warn('Нет координат для опоры:', object);
        return;
    }

  

    // Определяем тип трансформатора на основе type объекта или используем дефолтный 'transformer'
    const transformerType = object.type || 'transformer';
    
    // Создаем base64 строку с данными объекта
    const base64 = btoa(JSON.stringify({
        type: transformerType,
        id: object.fid
    }));

    // Формируем URL с параметром view
    const url = `${window.location.origin}/an?view=${encodeURIComponent(base64)}`;

    // Открываем новую вкладку
    window.open(url, '_blank');
};

const getStreamImageUrl = (auth, s3Id) => {

    const token = auth.user?.access_token;
    if (!token) return null;

    // Формируем URL с токеном в query параметре для stream endpoint
    return `${$authHost.defaults.baseURL}/apig/s3/files/${s3Id}/stream?token=${encodeURIComponent(token)}`;
};

const ImagesPreview = ({ images }) => {
    console.log(images)
    const auth = useAuth()
    const [activeIndex, setActiveIndex] = useState(null);
    const [visible, setVisible] = useState(false);
    const [previews, setPreviews] = useState([]);
    // Функция для получения превью изображения
    const fetchImagePreview = async (s3Id) => {
        try {
            const response = await $authHost.get(`/apig/s3/files/${s3Id}/preview`);
            return response.data;
        } catch (error) {
            console.error('Ошибка получения превью изображения:', error);
            return null;
        }
    };

    useEffect(() => {
        const fetchImagePreviews = async () => {
            console.log(images)
            const imagePromises = images.map(async (image) => {
                const previewData = await fetchImagePreview(image.id);
                if (previewData && previewData.success) {
                    return {
                        id: image.id,
                        preview: `data:${previewData.data.content_type};base64,${previewData.data.base64}`,
                        name: previewData.data.name,
                        user: previewData.data.user,
                        s3Id: image.id
                    };
                }
                return null;
            });

            const imagesData = await Promise.all(imagePromises);
            const validImages = imagesData.filter(img => img !== null);
            setPreviews(validImages);
        };
        console.log('fetchImagePreviews')
        if (images.length > 0) {

            fetchImagePreviews();
        }
    }, [images]);

    const handleImageClick = (index) => {
        setActiveIndex(index);
        setVisible(true);
    };

    return ([
        <ImageList cols={3} style={{ maxHeight: '300px', overflowY: 'auto', scrollbarWidth: 'thin' }}>
            {previews.map((image, i) => {
                return (
                    <ImageListItem key={image.id}>
                        <img src={image.preview} alt={image.name} loading="lazy" onClick={() => handleImageClick(i)} />
                        <ImageListItemBar
                            title={image.name}
                            subtitle={'@' + image.user}
                            sx={{
                                '& .MuiImageListItemBar-title': {
                                    fontSize: '0.7rem',
                                    color: '#FFF'
                                },
                                '& .MuiImageListItemBar-subtitle': {
                                    fontSize: '0.6rem',
                                    color: '#AAA'
                                }
                            }}
                        />
                    </ImageListItem>
                )
            })}
        </ImageList>,
        visible &&
        <Viewer
            visible={visible}
            onClose={() => setVisible(false)}
            images={previews.map(image => {
                console.log(image)
                const streamUrl = getStreamImageUrl(auth, image.id);
                return {
                    src: streamUrl,
                    alt: image.name,
                    downloadUrl: streamUrl
                };
            })}
            activeIndex={activeIndex}
            zIndex={9999}
            downloadable={true}
            scalable={true}
            rotatable={true}
            zoomable={true}
            drag={true}
            downloadInNewWindow={true}
        />
        ,
    ]);
};

const VideosPreview = ({ videos = [] }) => {
    const [selectedVideo, setSelectedVideo] = useState(null);
    const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleVideoClick = (video) => {
        setSelectedVideo(video);
        setIsFullscreenOpen(true);
    };

    const handleCloseFullscreen = () => {
        setIsFullscreenOpen(false);
        setSelectedVideo(null);
    };

    const getVideoUrl = (video) => {
        // Предполагаем, что video имеет свойство url или path
        return video.url || video.path || video.file_url || '';
    };

    const getVideoName = (video) => {
        return video.name || video.filename || video.original_name || 'Видео';
    };

    if (!videos || videos.length === 0) {
        return null;
    }

    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {videos.map((video, index) => {
                const videoUrl = getVideoUrl(video);
                const videoName = getVideoName(video);

                if (!videoUrl) {
                    return null;
                }

                return (
                    <Tooltip key={index} title={`${videoName} - Нажмите для просмотра`}>
                        <Chip
                            icon={<VideoIcon />}
                            label={`${videos.length > 1 ? `${index + 1}. ` : ''}${videoName}`}
                            onClick={() => handleVideoClick(video)}
                            clickable
                            variant="outlined"
                            sx={{
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: theme.palette.action.hover,
                                },
                                maxWidth: isMobile ? '150px' : '200px',
                                '& .MuiChip-label': {
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }
                            }}
                        />
                    </Tooltip>
                );
            })}

            {/* Полноэкранный диалог с видео */}
            <Dialog
                open={isFullscreenOpen}
                onClose={handleCloseFullscreen}
                maxWidth={false}
                fullWidth
                fullScreen={isMobile}
                sx={{
                    '& .MuiDialog-paper': {
                        margin: isMobile ? 0 : '32px',
                        width: isMobile ? '100%' : '90vw',
                        height: isMobile ? '100%' : '90vh',
                        maxWidth: 'none',
                        maxHeight: 'none',
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px 24px',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                    }}
                >
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        {selectedVideo ? getVideoName(selectedVideo) : 'Видео'}
                    </Typography>
                    <IconButton
                        onClick={handleCloseFullscreen}
                        sx={{ color: theme.palette.text.primary }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent
                    sx={{
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#000',
                        position: 'relative',
                    }}
                >
                    {selectedVideo && (
                        <Box
                            sx={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <video
                                controls
                                autoPlay
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                }}
                                onError={(e) => {
                                    console.error('Ошибка загрузки видео:', e);
                                }}
                            >
                                <source src={getVideoUrl(selectedVideo)} type="video/mp4" />
                                <source src={getVideoUrl(selectedVideo)} type="video/webm" />
                                <source src={getVideoUrl(selectedVideo)} type="video/ogg" />
                                Ваш браузер не поддерживает воспроизведение видео.
                            </video>
                        </Box>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

function rowContent(
    _index,
    row,
    {
        darkMode,
        visibleColumns,
        users
    }
) {
    // Если это padding элемент, возвращаем пустую строку
    if (row.isPadding) {
        // Получаем только видимые столбцы
        const visibleColumnsArray = columns.filter(col => visibleColumns[col.id]);
        return (
            <React.Fragment>
                {visibleColumnsArray.map((column) => (
                    <TableCell key={column.id} sx={{
                        height: '20px',
                        minHeight: '20px',
                        padding: '4px 8px',
                        border: 'none',
                        lineHeight: '20px'
                    }} />
                ))}
            </React.Fragment>
        );
    }

    // Проверяем, что у нас есть данные строки
    if (!row) return null;

    // Общий стиль для всех TableCell
    const cellStyle = {
        minHeight: '40px',
        height: 'auto',
        padding: '4px 8px',
        fontSize: '0.75rem',
        color: darkMode ? '#f5f7fa' : '#333',
        borderBottom: darkMode
            ? '1px solid rgba(255, 255, 255, 0.08)'
            : '1px solid rgba(0, 0, 0, 0.08)',
        borderRight: darkMode
            ? '1px solid rgba(255, 255, 255, 0.05)'
            : '1px solid rgba(0, 0, 0, 0.05)',
        fontSize: { xs: '0.7rem', md: '0.8rem' },
    };

    return (
        <React.Fragment>
            {/* № */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {row.id}
                </Typography>
            </TableCell>

            {/* Имя пользователя */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                    <Chip
                        label={users.find(user => user.id === row.user_id)?.name || ''}
                        size="small"
                        onClick={() => { }}
                        sx={{
                            bgcolor: darkMode ? 'rgba(211, 47, 47, 0.2)' : 'rgba(211, 47, 47, 0.1)',
                            color: darkMode ? '#f44336' : '#d32f2f',
                            fontSize: '0.7rem',
                            height: 'auto',
                            minHeight: '24px',
                            maxWidth: '100%',
                            cursor: 'auto',
                            transition: 'all 0.2s ease',
                            '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                textAlign: 'center',
                                lineHeight: '1.2',
                                padding: '4px 8px',
                            },
                        }}
                    />
                </Box>
            </TableCell>

            {/* Действие */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                <Chip
                    label={actions[row.action] || 'Не указано'}
                    size="small"
                    onClick={() => { }}
                    sx={{
                        bgcolor: darkMode ? 'rgba(171, 71, 188, 0.2)' : 'rgba(106, 27, 154, 0.1)',
                        color: darkMode ? '#D85AED' : '#7E20B8',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        height: 'auto',
                        minHeight: '24px',
                        maxWidth: '100%',
                        cursor: 'auto',
                        transition: 'all 0.2s ease',
                        '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            textAlign: 'center',
                            lineHeight: '1.2',
                            padding: '4px 8px',
                        },
                    }}
                />
            </TableCell>

            {/* Трансформатор */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                {row.transformer &&
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                openObjectView(row.transformer);
                            }}
                            sx={{
                                padding: '2px',
                                color: '#2196F3',
                                '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                    color: '#1976D2'
                                }
                            }}
                            title="Открыть обзор опоры в новой вкладке"
                        >
                            {/* <LocationOnIcon sx={{ fontSize: { xs: '12px', md: '16px' } }} /> */}
                            <Box sx={{ position: 'relative', width: 32, height: 32 }}>
                                <Box style={{ position: 'absolute', margin: '5px 0 0 6px' }}>
                                    {ktpIcon('default', 20, 20)}
                                </Box>
                                {[2, 3, 4, 7].includes(Number(row.transformer?.layer)) ? (
                                    <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                        {projectStatusOreolIcon('ktp', Number(row.transformer?.layer), 32)}
                                    </Box>
                                ) : null}
                            </Box>
                        </IconButton>
                        <Typography sx={{ fontSize: '0.875rem' }}>
                            {`${row.transformer?.name}${row.transformer?.town ? ' (' + row.transformer?.town + ')' : ''}`}
                        </Typography>
                    </Box>
                }
            </TableCell>

            {/* Опора */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            openObjectView(row.pillar);
                        }}
                        sx={{
                            padding: '2px',
                            color: '#2196F3',
                            '&:hover': {
                                backgroundColor: 'rgba(33, 150, 243, 0.1)',
                                color: '#1976D2'
                            }
                        }}
                        title="Открыть обзор опоры в новой вкладке"
                    >
                        {/* <LocationOnIcon sx={{ fontSize: { xs: '12px', md: '16px' } }} /> */}
                        <Box sx={{ position: 'relative', width: 28, height: 28 }}>
                            <Box style={{ position: 'absolute', margin: '4px 0 0 8px' }}>
                                {pillarIcon(row.pillar.type, row.pillar.status, row.pillar.material_type, row.pillar.supports_count, 12)}

                            </Box>
                            {[2, 3, 4, 7].includes(Number(row.pillar?.layer)) ? (
                                <Box style={{ position: 'absolute', margin: '0 0 0 0' }}>
                                    {projectStatusOreolIcon('pillar', Number(row.pillar.layer), 28)}
                                </Box>
                            ) : null}
                        </Box>
                    </IconButton>
                    <Typography sx={{ fontSize: '0.875rem' }}>
                        {row.pillar.name || 'Не указано'}
                    </Typography>
                </Box>
            </TableCell>

            {/* Дата */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.7rem', md: '0.8rem' } }}>
                    {row.dt ? new Date(row.dt).toLocaleTimeString('ru-RU') + ' ' + new Date(row.dt).toLocaleDateString('ru-RU') : 'Не указано'}
                </Typography>
            </TableCell>

            {/* Фото */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                {row.files?.filter(file => file.type === 'image').length > 0 ?
                    <ImagesPreview images={row.files?.filter(file => file.type === 'image') || []} /> : ''}
            </TableCell>

            {/* Видео */}
            <TableCell align="center" sx={{
                ...cellStyle,
                color: darkMode ? '#f5f7fa' : '#333'
            }}>
                {row.files?.filter(file => file.type === 'video').length > 0 ?
                    <VideosPreview videos={row.files?.filter(file => file.type === 'video') || []} /> : ''}
            </TableCell>

        </React.Fragment>
    );
}

// Утилиты для работы с localStorage
const STORAGE_KEYS = {
    FIELD_WORKS_SETTINGS: 'field_works_settings'
};

const getStoredSettings = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.FIELD_WORKS_SETTINGS);
        return stored ? JSON.parse(stored) : {};
    } catch {
        return {};
    }
};

const storeSettings = (settings) => {
    try {
        const current = getStoredSettings();
        const updated = { ...current, ...settings };
        localStorage.setItem(STORAGE_KEYS.FIELD_WORKS_SETTINGS, JSON.stringify(updated));
    } catch (error) {
        console.warn('Failed to save field works settings:', error);
    }
};

const FieldWorksView = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useSelector(state => state.state.user);
    const darkMode = useSelector(state => state.state.darkMode);
    const dispatch = useDispatch();

    // Получаем сохраненные настройки
    const storedSettings = getStoredSettings();

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [groupedData, setGroupedData] = useState({ groupCounts: [], groupedData: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [projects, setProjects] = useState([]);
    const [users, setUsers] = useState([]);

    // Состояния для поиска и фильтрации
    const [searchQuery, setSearchQuery] = useState('');
    // 'all', 'my', 'stage'
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [selectedProjects, setSelectedProjects] = useState(storedSettings.selectedProjects ?? []);

    // Состояния для продвинутой фильтрации
    const [advancedFilters, setAdvancedFilters] = useState(storedSettings.advancedFilters ?? []);
    const [filterMode, setFilterMode] = useState(storedSettings.filterMode ?? 'objects'); // 'objects' | 'codes'
    const availableFilters = useMemo(() => ([
        { id: 'user_name', label: 'Имя пользователя', type: 'multi-select', options: users.map(u => ({ id: u.id, name: u.name, label: u.name })) },
        { id: 'action', label: 'Действие', type: 'multi-select', options: Object.entries(actions).map(([key, value]) => ({ id: key, name: value, label: value })) },
        { id: 'transformer', label: 'Трансформатор', type: 'text' },
        { id: 'pillar', label: 'Опора', type: 'text' },
        { id: 'dt', label: 'Дата', type: 'date' },
        { id: 'images', label: 'Фотографии', type: 'boolean' },
        { id: 'videos', label: 'Видео', type: 'boolean' },

    ]), [users, actions]);
    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    // Состояние для меню экспорта
    const [exportMenuAnchor, setExportMenuAnchor] = useState(null);

    // Состояние для выбранных строк
    const [selectedRows, setSelectedRows] = useState(new Set());

    // Функция для фильтрации и сортировки данных
    const filterAndSortData = useCallback((rawData, search, projectsFilter, advancedFilters, sortBy, sortDirection) => {
        let filtered = [...rawData];

        // Поиск
        if (search.trim()) {
            // Разбиваем поисковый запрос на отдельные слова и фильтруем пустые
            const searchTerms = search.toLowerCase().split(/\s+/).filter(term => term.length > 0);
            filtered = filtered.filter(item => {
                return searchTerms.some(term => item.user?.name.toLowerCase().includes(term) || item.action.toLowerCase().includes(term) || item.transformer?.name.toLowerCase().includes(term) || item.transformer?.town.toLowerCase().includes(term) || item.pillar?.name.toLowerCase().includes(term) || item.dt.toLowerCase().includes(term));
            });


        }

        // Фильтрация по проектам
        if (projectsFilter && projectsFilter.length > 0) {
            filtered = filtered.filter(item => {
                return projectsFilter.includes(item.pillar?.kdp);
            });
        }

        if (advancedFilters && advancedFilters.length > 0) {
            advancedFilters.forEach(filter => {
                console.log(filter)
                switch (filter.id) {
                    case 'user_name':
                        if (filter.value && filter.value !== '') {
                            const userIds = filter.value.map(u => u.id);
                            filtered = filtered.filter(item => {
                                if (filter.logic === 'and') {
                                    return userIds.every(userId => item.user_id === userId);
                                } else {
                                    return userIds.some(userId => item.user_id === userId);
                                }
                            });
                        }
                        break;

                    case 'action':
                        if (filter.value && filter.value !== '') {
                            const actionIds = filter.value.map(u => u.id);
                            filtered = filtered.filter(item => {
                                if (filter.logic === 'and') {
                                    return actionIds.every(actionId => item.action === actionId);
                                } else {
                                    return actionIds.some(actionId => item.action === actionId);
                                }

                            });
                        }
                        break;

                    case 'transformer':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.filter(item => {
                                switch (filter.operator) {
                                    case 'contains':
                                        return `${item.transformer?.name}${item.transformer?.town ? ' (' + item.transformer?.town + ')' : ''}`.toLowerCase().includes(searchValue);
                                    case 'not_contains':
                                        return !`${item.transformer?.name}${item.transformer?.town ? ' (' + item.transformer?.town + ')' : ''}`.toLowerCase().includes(searchValue);
                                    case 'equals':
                                        return `${item.transformer?.name}${item.transformer?.town ? ' (' + item.transformer?.town + ')' : ''}`.toLowerCase() === searchValue;
                                    case 'not_equals':
                                        return `${item.transformer?.name}${item.transformer?.town ? ' (' + item.transformer?.town + ')' : ''}`.toLowerCase() !== searchValue;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;
                    case 'pillar':
                        if (filter.value && filter.value.trim() !== '') {
                            const searchValue = filter.value.toLowerCase();
                            filtered = filtered.filter(item => {
                                switch (filter.operator) {
                                    case 'contains':
                                        return item.pillar.name.toLowerCase().includes(searchValue);
                                    case 'not_contains':
                                        return !item.pillar?.name.toLowerCase().includes(searchValue);
                                    case 'equals':
                                        return item.pillar.name.toLowerCase() === searchValue;
                                    case 'not_equals':
                                        return item.pillar.name.toLowerCase() !== searchValue;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'dt':
                        if (filter.value && filter.value.trim() !== '') {
                            const filterDate = new Date(filter.value);
                            filtered = filtered.filter(item => {
                                const itemDate = new Date(item.dt);
                                if (!itemDate || isNaN(itemDate.getTime())) return false;

                                switch (filter.operator) {
                                    case 'equals':
                                        return itemDate.getDate() === filterDate.getDate() &&
                                            itemDate.getMonth() === filterDate.getMonth() &&
                                            itemDate.getFullYear() === filterDate.getFullYear();
                                    case 'greater':
                                        return itemDate > filterDate;
                                    case 'less':
                                        return itemDate < filterDate;
                                    case 'between':
                                        if (!filter.value2) return true;
                                        const filterDate2 = new Date(filter.value2);
                                        return itemDate >= filterDate && itemDate <= filterDate2;
                                    default:
                                        return true;
                                }
                            });
                        }
                        break;

                    case 'images':
                        if (filter.value && filter.value.trim() !== '') {
                            const filterValue = filter.value === 'true';
                            filtered = filtered.filter(item => {
                                const imagesCount = item.files ? item.files.filter(file => file.type === 'image').length : 0;
                                if (filterValue) {
                                    return imagesCount > 0;
                                } else {
                                    return imagesCount === 0;
                                }
                            });

                        }
                        break;

                    case 'videos':
                        if (filter.value && filter.value.trim() !== '') {
                            const filterValue = filter.value === 'true';
                            filtered = filtered.filter(item => {
                                const videosCount = item.files ? item.files.filter(file => file.type === 'video').length : 0;
                                if (filterValue) {
                                    return videosCount > 0;
                                } else {
                                    return videosCount === 0;
                                }
                            });

                        }
                        break;

                }


            });
        }


        // Сортировка
        if (sortBy) {
            filtered.sort((a, b) => {
                let aValue, bValue;

                switch (sortBy) {
                    case 'id':
                        aValue = a.id || 0;
                        bValue = b.id || 0;
                        break;
                    case 'user_name':
                        aValue = (a.user?.name || '').toLowerCase();
                        bValue = (b.user?.name || '').toLowerCase();
                        break;
                    case 'action':
                        aValue = a.action || 0;
                        bValue = b.action || 0;
                        break;
                    case 'transformer':
                        aValue = (a.transformer?.name || '').toLowerCase();
                        bValue = (b.transformer?.name || '').toLowerCase();
                        break;
                    case 'pillar':
                        aValue = (a.pillar?.name || '').toLowerCase();
                        bValue = (b.pillar?.name || '').toLowerCase();
                        break;
                    case 'dt':
                        // Сортируем по плановому началу, если нет - по фактическому
                        aValue = a.dt ? new Date(a.dt).getTime() : 0;
                        bValue = b.dt ? new Date(b.dt).getTime() : 0;
                        break;
                    case 'images':
                        aValue = a.files?.filter(file => file.type === 'image').length || 0;
                        bValue = b.files?.filter(file => file.type === 'image').length || 0;
                        break;
                    case 'videos':
                        aValue = a.files?.filter(file => file.type === 'video').length || 0;
                        bValue = b.files?.filter(file => file.type === 'video').length || 0;
                        break;
                    default:
                        return 0;
                }

                // Обработка значений null/undefined
                if (aValue === null || aValue === undefined) aValue = sortBy === 'id' ? 0 : '';
                if (bValue === null || bValue === undefined) bValue = sortBy === 'id' ? 0 : '';

                // Для числовых значений
                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    const comparison = aValue - bValue;
                    return sortDirection === 'desc' ? -comparison : comparison;
                }

                // Для строковых значений
                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    const comparison = aValue.localeCompare(bValue, 'ru');
                    return sortDirection === 'desc' ? -comparison : comparison;
                }

                return 0;
            });
        } else {
            filtered.sort((a, b) => (new Date(b.dt).getTime() || 0) - (new Date(a.dt).getTime() || 0));
          
        }

        return filtered;
    }, [user.id]);

    // Состояния для сортировки (объявляем здесь, чтобы они были доступны в useEffect ниже)
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'

    // Обновляем отфильтрованные данные при изменении исходных данных или параметров
    useEffect(() => {
        const filtered = filterAndSortData(data, searchQuery, selectedProjects, advancedFilters, sortBy, sortDirection);
        setFilteredData(filtered);
    }, [data, searchQuery, selectedProjects, advancedFilters, sortBy, sortDirection, filterAndSortData]);


    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            let response;
            if (user.roles.some(role => ['admin', 'project_manager'].includes(role))) {
                response = await $authHost.get('/apig/field-works');
            } else {
                response = await $authHost.get('/apig/field-works/user/' + user.id);
            }
            const { success, data } = response.data;
            setData(success ? data : []);
        } catch (error) {
            console.error('Error fetching data:', error);
            setData([]);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchData()
    }, [user]);


    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await $authHost.get('/apig/users');
                const { success, data } = response.data;

                setUsers(success ? data.map(item => ({
                    id: item.id,
                    name: item.display_name
                })) : []);
            } catch (e) {
                setUsers([])
                console.log(e)
                setError(e.message);
            }
        };
        fetchUsers();
    }, []);






    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await $authHost.get('/apig/reestr/projects');
                const { success, data } = response.data;
                setProjects(success ? data : []);
            } catch (e) {
                setProjects([])
                console.log(e)
                setError(e.message);
            }
        };
        fetchProjects();
    }, []);


    const activeProjects = useMemo(() => {
        return projects.filter(project => data.some(item => item.pillar?.kdp === project.id));
    }, [projects, data]);


    // Функция для подготовки данных для группировки
    const prepareGroupedData = useCallback((filteredData, selectedProjects, projectsList) => {
        console.log(filteredData, selectedProjects, projectsList)
        // Если не выбран ни один КДП, показываем все данные без группировки
        if (!selectedProjects || selectedProjects.length === 0) {
            return {
                groupCounts: [],
                groupedData: []
            };
        }
        // Группируем по КДП
        const groupedByKdp = {};

        filteredData.forEach(item => {
            const itemProjectId = item.pillar?.kdp;
            const projectName = projectsList.find(p => p.id === itemProjectId)?.name || 'Неизветсный проект';

            if (!groupedByKdp[projectName]) {
                groupedByKdp[projectName] = [];
            }
            groupedByKdp[projectName].push(item);
        });

        // Сортируем группы по названию КДП
        const sortedGroups = Object.keys(groupedByKdp).sort();

        // Создаем плоский массив данных с информацией о группе
        const groupedData = [];
        const groupCounts = [];

        sortedGroups.forEach((groupName, groupIndex) => {
            const groupItems = groupedByKdp[groupName];
            groupCounts.push(groupItems.length);

            groupItems.forEach(item => {
                groupedData.push({
                    ...item,
                    group: groupIndex,
                    groupName: groupName
                });
            });
        });
        // Добавляем пустые элементы в конец для принудительного рендеринга
        const paddingItems = Array.from({ length: sortedGroups.length }, (_, index) => ({
            id: `padding-${index}`,
            fid: groupedData.length + index + 1,
            group: groupedData.length > 0 ? groupedData[groupedData.length - 1].group : 0,
            groupName: groupedData.length > 0 ? groupedData[groupedData.length - 1].groupName : 'Все проекты',
            isPadding: true
        }));

        return { groupCounts, groupedData: [...groupedData, ...paddingItems] };
    }, []);

    // Обновляем группированные данные при изменении отфильтрованных данных
    useEffect(() => {
        const grouped = prepareGroupedData(filteredData, selectedProjects, activeProjects);
        console.log(grouped)
        setGroupedData(grouped);
    }, [filteredData, selectedProjects, activeProjects, prepareGroupedData]);

    const [stages, setStages] = useState([1, 2, 3, 4, 5, 6, 7, 8, 9, 10]);
    const [objectDialogOpen, setObjectDialogOpen] = useState(false);
    const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
    const [selectedObject, setSelectedObject] = useState(null);
    const [editMode, setEditMode] = useState(false)
    const [editing, setEditing] = useState({ id: null, field: null })
    const [editedRows, setEditedRows] = useState([])



    // Состояние для управления видимостью столбцов
    const [visibleColumns, setVisibleColumns] = useState(
        storedSettings.visibleColumns ?? columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {})
    );
    const [columnMenuAnchor, setColumnMenuAnchor] = useState(null);
    const [columnWidths, setColumnWidths] = useState({});

    // Сохранение настроек в localStorage при изменении
    useEffect(() => {
        storeSettings({ darkMode });
    }, [darkMode]);

    useEffect(() => {
        storeSettings({ selectedProjects });
    }, [selectedProjects]);

    useEffect(() => {
        storeSettings({ visibleColumns });
    }, [visibleColumns]);

    // useEffect для пересчета ширин столбцов будет добавлен после определения функции getColumnWidths
















    // Обработчик сортировки
    const handleSort = (columnId) => {
        if (sortBy === columnId) {
            // Переключаем направление сортировки
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // Устанавливаем новый столбец для сортировки
            setSortBy(columnId);
            setSortDirection('asc');
        }

        // Добавляем небольшую задержку для лучшего UX
        setTimeout(() => {
            // Принудительно обновляем отфильтрованные данные
            const filtered = filterAndSortData(data, searchQuery, selectedProjects, advancedFilters, columnId, sortBy === columnId ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc');
            setFilteredData(filtered);
        }, 0);
    };

    // Обработчики для управления видимостью столбцов
    const handleColumnMenuOpen = (event) => {
        setColumnMenuAnchor(event.currentTarget);
    };

    const handleColumnMenuClose = () => {
        setColumnMenuAnchor(null);
    };

    const handleColumnVisibilityChange = (columnId, visible) => {
        setVisibleColumns(prev => ({
            ...prev,
            [columnId]: visible
        }));
    };

    const handleShowAllColumns = () => {
        setVisibleColumns(columns.reduce((acc, col) => ({ ...acc, [col.id]: true }), {}));
    };

    const handleHideAllColumns = () => {
        // Оставляем хотя бы первый столбец видимым
        const newVisibility = columns.reduce((acc, col, index) => ({
            ...acc,
            [col.id]: index === 0
        }), {});
        setVisibleColumns(newVisibility);
    };

    // Получаем только видимые столбцы
    const getVisibleColumns = useCallback(() => {
        return columns.filter(col => visibleColumns[col.id]);
    }, [visibleColumns]);

    // Функция для вычисления динамической ширины столбцов
    const getColumnWidths = useCallback(() => {
        const visibleCols = columns.filter(col => visibleColumns[col.id]);
        if (visibleCols.length === 0) return {};

        // Получаем ширину экрана
        const screenWidth = window.innerWidth;
        const containerPadding = 32; // Отступы контейнера
        const availableWidth = screenWidth - containerPadding;

        // Вычисляем сумму минимальных ширин
        const totalMinWidth = visibleCols.reduce((sum, col) => sum + col.minWidth, 0);

        // Если доступная ширина больше суммы минимальных ширин, распределяем оставшееся место
        if (availableWidth > totalMinWidth) {
            const extraWidth = availableWidth - totalMinWidth;
            const flexibleCols = visibleCols.filter(col => !['fid'].includes(col.id)); // Не растягиваем узкие столбцы
            const extraPerCol = flexibleCols.length > 0 ? extraWidth / flexibleCols.length : 0;

            return visibleCols.reduce((acc, col) => {
                const isFlexible = flexibleCols.includes(col);
                acc[col.id] = isFlexible ? col.minWidth + extraPerCol : col.minWidth;
                return acc;
            }, {});
        }

        // Если места мало, используем минимальные ширины
        return visibleCols.reduce((acc, col) => {
            acc[col.id] = col.minWidth;
            return acc;
        }, {});
    }, [visibleColumns]);

    // Пересчитываем ширины столбцов при изменении видимости
    useEffect(() => {
        setColumnWidths(getColumnWidths());
    }, [getColumnWidths]);

    // Пересчитываем ширины при изменении размера окна
    useEffect(() => {
        const handleResize = () => {
            setColumnWidths(getColumnWidths());
        };

        window.addEventListener('resize', handleResize);
        // Начальный расчет
        setColumnWidths(getColumnWidths());

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [getColumnWidths]);

    // Функции для управления продвинутыми фильтрами
    const handleApplyAdvancedFilters = () => {
        // Фильтры применяются автоматически через useEffect
        setShowAdvancedFilters(false);
    };

    const handleClearAdvancedFilters = () => {
        setAdvancedFilters([]);
        storeSettings({ advancedFilters: [] });
    };

    const getActiveAdvancedFiltersCount = () => {
        return advancedFilters.length;
    };

    const addFilter = (filterId) => {
        const filter = availableFilters.find(f => f.id === filterId);
        if (filter) {
            const newFilter = {
                id: filterId,
                label: filter.label,
                type: filter.type,
                options: filter.options,
                operator: filter.type === 'date' || filter.type === 'number' ? 'equals' :
                    filter.type === 'text' ? 'contains' : null,
                value: filter.type === 'date' || filter.type === 'number' ? null :
                    filter.type === 'boolean' ? '' :
                        filter.type === 'text' ? '' :
                            filter.type === 'multi-select' ? [] : [],
                value2: filter.type === 'date' ? null : null, // Для фильтра "между"
                logic: filter.type === 'multi-select' ? 'or' : null,
                enabled: true
            };
            const newFilters = [...advancedFilters, newFilter];
            setAdvancedFilters(newFilters);
            storeSettings({ advancedFilters: newFilters, filterMode });
        }
    };


    const removeFilter = (filterId) => {
        const newFilters = advancedFilters.filter(f => f.id !== filterId);
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters, filterMode });
    };

    const updateFilter = (filterId, updates) => {
        const newFilters = advancedFilters.map(f =>
            f.id === filterId ? { ...f, ...updates } : f
        );
        setAdvancedFilters(newFilters);
        storeSettings({ advancedFilters: newFilters, filterMode });
    };

    const getAvailableFiltersForSelect = () => {
        const usedFilterIds = advancedFilters.map(f => f.id);
        return availableFilters.filter(f => !usedFilterIds.includes(f.id));
    };

    // Функции для управления меню экспорта
    const handleExportMenuOpen = (event) => {
        setExportMenuAnchor(event.currentTarget);
    };

    const handleExportMenuClose = () => {
        setExportMenuAnchor(null);
    };

    const handleExportFormatSelect = (format) => {
        handleExportMenuClose();
        if (format === 'excel') {
            exportToExcel();
        } else if (format === 'pdf') {
            exportToPDF();
        }
    };
    // Функция экспорта в Excel с использованием ExcelJS
    const exportToExcel = async () => {
        try {
            // Группируем данные по проектам
            const groupedByProjects = {};
            filteredData.forEach(item => {
                const projectId = item.pillar.kdp;
                const project = projects.find(p => p.id === projectId);
                const projectName = project?.name || 'Неизвестный проект';

                if (!groupedByProjects[projectName]) {
                    groupedByProjects[projectName] = [];
                }
                groupedByProjects[projectName].push(item);
            });

            // Сортируем группы по названию проекта
            const sortedGroups = Object.keys(groupedByProjects).sort();

            // Создаем новую рабочую книгу ExcelJS
            const workbook = new ExcelJS.Workbook();
            workbook.created = new Date();
            workbook.modified = new Date();
            workbook.title = 'Лог полевых работ';
            workbook.subject = 'Экспорт данных лога полевых работ';

            // Создаем лист
            const worksheet = workbook.addWorksheet('Лог полевых работ');

            // Закрепляем первую строку (общие заголовки)
            worksheet.views = [
                { state: 'frozen', ySplit: 1 }
            ];


            // Определяем заголовки
            const headers = [
                '№', 'Имя пользователя', 'Действие', 'Трансформатор', 'Опора', 'Дата', 'Фотографии', 'Видео'
            ];

            // Собираем все данные в один массив
            let allRows = [];
            let projectRows = []

            // Добавляем данные всех проектов
            sortedGroups.forEach((projectName, projectIndex) => {
                const projectData = groupedByProjects[projectName];

                // Добавляем строку проекта
                const projectRow = [projectName];
                allRows.push(projectRow);
                projectRows.push(allRows.length + 1)

                // Добавляем данные объектов проекта
                const tableRows = projectData.map(item => ([
                    item.id || '',
                    users.find(u => u.id === item.user_id)?.name || '',
                    actions[item.action] || '',
                    item.transformer ? `${item.transformer?.name}${item.transformer?.town ? ' (' + item.transformer?.town + ')' : ''}` : '',
                    item.pillar?.name || '',
                    item.dt ? formatDate(item.dt, false) : '',
                    item.files?.filter(file => file.type === 'image').length || '',
                    item.files?.filter(file => file.type === 'video').length || '',
                ]));

                allRows.push(...tableRows);
            });

            console.log(allRows);
            console.log(projectRows);

            // Добавляем одну большую таблицу со всеми данными
            const endRow = allRows.length + 1; // +1 для заголовков
            worksheet.addTable({
                name: 'FieldWorksTable',
                ref: `A1:G${endRow}`,
                headerRow: true,
                totalsRow: false,
                style: { theme: 'TableStyleLight3', showRowStripes: true },
                columns: headers.map(h => ({ name: h, filterButton: true })),
                rows: allRows
            });
            const colWidths = [8, 20, 20, 20, 20, 20, 20, 20]
            const centerCols = [1, 2, 3, 4, 5, 6, 7, 8]


            // Устанавливаем ширину столбцов
            colWidths.forEach((width, index) => {
                const column = worksheet.getColumn(index + 1);
                column.width = width;
            });

            const dateCols = [6]
            dateCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = 'dd/mm/yyyy'
            })

            const numberCols = [1, 7, 8]
            numberCols.forEach(colNumber => {
                const column = worksheet.getColumn(colNumber);
                column.numFmt = '0'
            })


            // const textCols = [2, 3, 7, 8, 9, 10, 15]
            // textCols.forEach(colNumber => {
            //     const column = worksheet.getColumn(colNumber);
            //     column.numFmt = ''
            // })

            // Устанавливаем черный цвет текста для всех ячеек таблицы
            for (let rowNumber = 1; rowNumber <= endRow; rowNumber++) {
                const row = worksheet.getRow(rowNumber);
                // row.height = undefined

                row.eachCell((cell, colNumber) => {
                    cell.font = {
                        ...cell.font,
                        name: 'Arial',
                        size: projectRows.includes(rowNumber) || rowNumber === 1 ? 11 : 10,
                        bold: projectRows.includes(rowNumber) || rowNumber === 1,
                        color: { argb: 'FF000000' } // Черный цвет

                    };
                    cell.alignment = {
                        horizontal: !projectRows.includes(rowNumber) && (rowNumber === 1 || centerCols.includes(colNumber)) ? 'center' : 'left',
                        vertical: 'middle',
                        wrapText: !projectRows.includes(rowNumber) || rowNumber == 1 ? true : false
                    }
                    // cell.numFmt = dateCols.includes(colNumber) ? 'dd.mm.yyyy' : ''

                    // cell.effectiveType = dateCols.includes(colNumber) ? 4 : 0


                });
                row.height = undefined; // Это включает автоподбор

                if (typeof row.commit === 'function') row.commit();
            }






            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Лог_полевых_работ_${dateStr}.xlsx`;

            // Экспортируем файл
            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.click();
            window.URL.revokeObjectURL(url);

            console.log('Excel файл успешно создан с примененными стилями (ExcelJS)');
        } catch (error) {
            console.error('Ошибка при экспорте в Excel:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };

    // Функция экспорта в PDF
    const exportToPDF = () => {
        try {
            // Группируем данные по проектам
            const groupedByProjects = {};
            filteredData.forEach(item => {
                const projectId = item.pillar.kdp;
                const project = projects.find(p => p.id === projectId);
                const projectName = project?.name || 'Неизвестный проект';

                if (!groupedByProjects[projectName]) {
                    groupedByProjects[projectName] = [];
                }
                groupedByProjects[projectName].push(item);
            });

            // Сортируем группы по названию проекта
            const sortedGroups = Object.keys(groupedByProjects).sort();

            // Создаем PDF документ
            const pdf = new jsPDF('landscape', 'mm', 'a4');
            pdf.setFont("arial", "normal");
            pdf.setLanguage('ru')

            // Настройки страницы
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const margin = 10;
            const contentWidth = pageWidth - 2 * margin;
            const contentHeight = pageHeight - 2 * margin;

            // Заголовок документа
            pdf.setFontSize(16);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(25, 118, 210);
            pdf.text('Лог полевых работ', pageWidth / 2, 10, { align: 'center' });

            // Информация о документе
            pdf.setFontSize(10);
            pdf.setFont("arial", "normal");
            pdf.setTextColor(100, 100, 100);
            const currentDate = new Date().toLocaleDateString('ru-RU');
            pdf.text(`Дата экспорта: ${currentDate}`, pageWidth - margin, 15, { align: 'right' });
            pdf.text(`Всего записей: ${filteredData.length}`, margin, 15);

            let currentY = 20;
            let pageNumber = 1;
            let totalPages = 1; // Будет обновлено после создания всех страниц

            // Определяем заголовки таблицы (как в Excel)
            const headers = [
                '№', 'Имя пользователя', 'Действие', 'Трансформатор', 'Опора', 'Дата', 'Фотографии', 'Видео'
            ];

            // Добавляем данные по группам
            sortedGroups.forEach((projectName, projectIndex) => {
                const projectData = groupedByProjects[projectName];

                // Проверяем, нужно ли добавить новую страницу
                if (currentY > pageHeight - 80) {
                    pdf.addPage();
                    pageNumber++;
                    currentY = 10;

                    // Начинаем с верхней части новой страницы
                    currentY = 10;
                }

                // Заголовок проекта (как в Excel - с заливкой)
                pdf.setFontSize(11);
                pdf.setFont("arial", "bold");
                pdf.setTextColor(0, 0, 0);

                // Рисуем фон для заголовка проекта (светло-голубой как в Excel)
                pdf.setFillColor(230, 243, 255); // E6F3FF
                pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 8, 'F');

                // Рисуем границы
                pdf.setDrawColor(208, 215, 224); // D0D7E0
                pdf.setLineWidth(0.1);
                pdf.rect(margin, currentY - 3, pageWidth - 2 * margin, 8);

                pdf.text(projectName, pageWidth / 2, currentY + 2, { align: 'center' });
                currentY += 12;

                // Подготавливаем данные для таблицы (как в Excel)
                const tableData = projectData.map((item, index) => [
                    item.id || '',
                    users.find(u => u.id === item.user_id)?.name || '',
                    actions[item.action] || '',
                    item.transformer ? `${item.transformer?.name}${item.transformer?.town ? ' (' + item.transformer?.town + ')' : ''}` : '',
                    item.pillar?.name || '',
                    item.dt ? formatDate(item.dt, false) : '',
                    item.files?.filter(file => file.type === 'image').length || '',
                    item.files?.filter(file => file.type === 'video').length || '',
                ]);

                // Добавляем таблицу с улучшенным форматированием
                autoTable(pdf, {
                    head: [headers],
                    body: tableData,
                    startY: currentY,
                    margin: { left: margin, right: margin },
                    pageBreak: 'auto',
                    rowPageBreak: 'avoid',
                    styles: {
                        fontSize: 8,
                        cellPadding: 1,
                        lineColor: [208, 215, 224], // D0D7E0 - как в Excel
                        lineWidth: 0.1,
                        textColor: [0, 0, 0],
                        font: 'arial',
                        fontStyle: 'normal'
                    },
                    headStyles: {
                        fillColor: [25, 118, 210], // Синий заголовок как в Excel
                        textColor: [255, 255, 255],
                        font: 'arial',
                        fontStyle: 'bold',
                        fontSize: 9,
                        cellPadding: 1,
                        halign: 'center',
                        valign: 'middle'
                    },
                    alternateRowStyles: {
                        fillColor: [230, 243, 255] // E6F3FF - светло-голубой для четных строк
                    },
                    columnStyles: {
                        0: { cellWidth: 15, halign: 'center' },   // №
                        1: { cellWidth: 40, halign: 'center' },  // Имя пользователя
                        2: { cellWidth: 40, halign: 'center' },  // Действие
                        3: { cellWidth: 40, halign: 'center' },  // Трансформатор
                        3: { cellWidth: 40, halign: 'center' },  // Опора
                        4: { cellWidth: 40, halign: 'center' },  // Дата
                        5: { cellWidth: 30, halign: 'center' },  // Фотографии
                        6: { cellWidth: 30, halign: 'center' },  // Видео

                    },
                    didDrawPage: function (data) {
                        // Функция будет вызвана для каждой страницы, но номера добавим позже
                    }
                });

                // Получаем позицию после таблицы
                const tableEndY = pdf.lastAutoTable ? pdf.lastAutoTable.finalY : currentY + 50;
                currentY = tableEndY + 5;

                // Не добавляем разделитель между проектами (как в Excel)
            });

            // Получаем общее количество страниц
            totalPages = pdf.getNumberOfPages();

            // Добавляем номера страниц на все страницы
            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                pdf.setPage(pageNum);

                // Добавляем номер страницы внизу
                pdf.setFontSize(8);
                pdf.setFont("arial", "normal");
                pdf.setTextColor(100, 100, 100);
                pdf.text(`Стр. ${pageNum} из ${totalPages}`, pageWidth - margin, pageHeight - 5, { align: 'right' });
            }

            // Генерируем имя файла с текущей датой
            const now = new Date();
            const dateStr = now.toISOString().split('T')[0];
            const fileName = `Лог_полевых_работ_${dateStr}.pdf`;
            // Сохраняем PDF
            pdf.save(fileName);
            console.log('PDF файл успешно создан с форматированием как в Excel');
        } catch (error) {
            console.error('Ошибка при экспорте в PDF:', error);
            alert('Произошла ошибка при экспорте файла');
        }
    };

    return (
        <Box sx={{
            bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',

            p: 0,
            m: 0,
            display: 'flex',
            flex: 1,
            height: '100%',
            flexDirection: 'column',
            // overflow: 'hidden',
            width: '100%'

        }}>
            <Paper sx={{
                display: 'flex',
                flexDirection: 'column',
                bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                borderRadius: 0,
                boxShadow: 'none',
                overflow: 'hidden',
                border: 'none',
                width: '100%',
                minHeight: '100%',
            }}>
                {/* Первая строка - заголовок, поиск, темная тема */}
                <Box sx={{
                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    p: { xs: 0.5, md: 1 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: { xs: 1, md: 2 },
                }}>
                    {/* Левая часть - заголовок */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: { xs: '100%', md: 'auto' } }}>
                        {/* <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 48,
                            height: 48,
                            borderRadius: '50%',
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                            border: darkMode ? '2px solid #64b5f6' : '2px solid #1976d2'
                        }}>
                            <BusinessIcon sx={{
                                fontSize: '28px',
                                color: darkMode ? '#64b5f6' : '#1976d2'
                            }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" sx={{
                                fontWeight: 700,
                                color: darkMode ? '#f5f7fa' : '#333',
                                mb: 0.5,
                            }}>
                                Реестр объектов
                            </Typography>
                            <Typography variant="body2" sx={{
                                color: darkMode ? '#bbb' : '#666',
                                fontSize: '0.875rem'
                            }}>
                                Управление объектами строительства
                            </Typography>
                        </Box> */}
                        {/* KDP селект */}
                        <FormControl
                            size="small"
                            sx={{
                                minWidth: { xs: 'auto', md: '300px' },
                                flex: { xs: 1, sm: 'none' },
                                '& .MuiInputLabel-root': {
                                    color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                    '&.Mui-focused': {
                                        color: '#1976d2'
                                    }
                                },
                            }}
                        >
                            {/* <InputLabel>Проект</InputLabel> */}
                            <Select
                                multiple
                                value={selectedProjects}
                                onChange={(e) => {
                                    const { target: { value } } = e;
                                    if (value.includes(0)) {
                                        if (selectedProjects.length === activeProjects.length) {
                                            setSelectedProjects([]);
                                        } else {
                                            setSelectedProjects(activeProjects.map(project => project.id));
                                        }
                                    } else {
                                        setSelectedProjects(
                                            typeof value === 'string' ? value.split(',').map(Number) : value,
                                        );
                                    }
                                }}
                                // label="Проект"
                                displayEmpty
                                // placeholder="Не выбрано"
                                renderValue={(selected) => {
                                    if (selected.length === 0) {
                                        return "Выбор проектов";
                                    }
                                    if (selected.length === activeProjects.length) {
                                        return "Все проекты"
                                    }
                                    if (selected.length === 1) {
                                        const project = activeProjects.find(project => project.id === selected[0]);
                                        return project ? project.name : `Выбрано: ${selected.length}`;
                                    }
                                    return `Выбрано: ${selected.length}`;
                                }}
                                sx={{
                                    fontSize: { xs: '0.8rem', md: '0.9rem' },
                                    bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#1976d2'
                                    },
                                    '& .MuiSelect-icon': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)'
                                    },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        '&.Mui-focused': {
                                            color: '#1976d2'
                                        },
                                        '&.MuiInputLabel-shrink': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        }
                                    },
                                    '& .MuiSelect-select': {
                                        '&:empty::before': {
                                            content: 'attr(placeholder)',
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        }
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {

                                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                            border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                            '& .MuiMenuItem-root': {

                                                color: darkMode ? '#f5f7fa' : '#333',
                                                '&:hover': {
                                                    bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                }
                                            },
                                            '& .MuiTypography-root': {
                                                fontSize: { xs: '0.8rem', md: '0.9rem' },
                                            },
                                            '& .MuiButtonBase-root': {
                                                minHeight: 0,
                                                padding: '4px'
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value={0}>
                                    <Checkbox
                                        size="small"
                                        checked={selectedProjects.length === activeProjects.length && activeProjects.length > 0}
                                        sx={{
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                            '&.Mui-checked': {
                                                color: darkMode ? '#64b5f6' : '#1976d2'
                                            }
                                        }}
                                    />
                                    <ListItemText primary="Все проекты" />
                                </MenuItem>
                                {activeProjects.map((project) => (
                                    <MenuItem key={project.id} value={project.id}>
                                        <Checkbox
                                            size="small"
                                            checked={selectedProjects.includes(project.id)}
                                            sx={{
                                                color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)',
                                                '&.Mui-checked': {
                                                    color: darkMode ? '#64b5f6' : '#1976d2'
                                                }
                                            }}
                                        />
                                        <ListItemText primary={project.name} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>

                    {/* Центральная часть - поиск и KDP селект */}
                    <Box sx={{
                        flex: 1,
                        display: 'flex',
                        gap: 2,
                        maxWidth: { xs: '100%', md: '600px' },
                        minWidth: { xs: '100%', sm: '400px', md: '500px' },
                        order: { xs: 3, md: 0 }
                    }}>


                        {/* Поиск */}
                        <Box sx={{
                            flex: 1,
                            position: 'relative'
                        }}>
                            <TextField
                                size="small"
                                placeholder="Поиск объектов..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                fullWidth
                                slotProps={{
                                    input: {
                                        startAdornment: <SearchIcon sx={{
                                            mr: 1,
                                            color: darkMode ? '#bbb' : 'text.secondary',
                                            fontSize: { xs: '16px', md: '20px' }
                                        }} />,
                                        endAdornment: searchQuery && (
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchQuery('')}
                                                sx={{
                                                    color: darkMode ? '#bbb' : '#666',
                                                    '&:hover': {
                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                    }
                                                }}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        )
                                    }
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: darkMode ? '#1e1e1e' : '#f8fafc',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.8rem', md: '0.9rem' },
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&::placeholder': {
                                            color: darkMode ? '#bbb' : '#666',
                                            opacity: 1
                                        }
                                    }
                                }}
                            />
                            {/* Индикатор результатов поиска */}
                            {/* {searchQuery && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        mt: 0.5,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                    }}
                                >
                                    Найдено: {filteredData.length} из {data.length}
                                </Typography>
                            )} */}
                        </Box>
                        {/* Правая часть - кнопки управления */}
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {/* Кнопка настроек столбцов */}
                            <Tooltip title="Настроить видимость столбцов">
                                <IconButton
                                    onClick={handleColumnMenuOpen}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#666',
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <ViewColumnIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />
                                </IconButton>
                            </Tooltip>

                            {/* Кнопка темной темы */}
                            {/* <IconButton
                                onClick={() => dispatch(toggleDarkMode())}
                                sx={{
                                    color: darkMode ? '#f5f7fa' : '#666',
                                    bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                {darkMode ? <LightModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} /> : <DarkModeIcon sx={{ fontSize: { xs: '16px', md: '20px' } }} />}
                            </IconButton> */}
                        </Box>
                    </Box>


                </Box>

                {/* Вторая строка - панель инструментов */}
                <Box sx={{
                    p: { xs: 1, md: 1 },
                    borderBottom: darkMode
                        ? '1px solid rgba(255, 255, 255, 0.12)'
                        : '1px solid rgba(0, 0, 0, 0.12)',
                    bgcolor: darkMode ? '#252525' : '#f8fafc',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: { xs: 1, md: 2 },
                }}>
                    {/* Индикатор активных фильтров */}
                    {/* {(searchQuery || filterMode !== 'all' || sortMode !== 'created_desc' || selectedKdp.length > 0) && (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flexWrap: 'wrap',
                            p: 1,
                            bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                            borderRadius: 1,
                            border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                        }}>
                            <Typography variant="caption" sx={{ color: darkMode ? '#64b5f6' : '#1976d2', fontWeight: 600 }}>
                                Активные фильтры:
                            </Typography>
                            {searchQuery && (
                                <Chip
                                    label={`Поиск: "${searchQuery}"`}
                                    size="small"
                                    onDelete={() => setSearchQuery('')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {selectedKdp.length > 0 && (
                                <Chip
                                    label={`KDP: ${selectedKdp.length} выбрано`}
                                    size="small"
                                    onDelete={() => setSelectedKdp([])}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {filterMode !== 'all' && (
                                <Chip
                                    label={`Фильтр: ${filterMode === 'my' ? 'Мои объекты' : 'Активные этапы'}`}
                                    size="small"
                                    onDelete={() => setFilterMode('all')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(156, 39, 176, 0.2)' : 'rgba(156, 39, 176, 0.1)',
                                        color: darkMode ? '#ce93d8' : '#9c27b0',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                            {sortMode !== 'created_desc' && (
                                <Chip
                                    label={`Сортировка: ${sortMode}`}
                                    size="small"
                                    onDelete={() => setSortMode('created_desc')}
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(76, 175, 80, 0.2)' : 'rgba(76, 175, 80, 0.1)',
                                        color: darkMode ? '#81c784' : '#388e3c',
                                        fontSize: '0.7rem',
                                    }}
                                />
                            )}
                        </Box>
                    )} */}

                    {/* Основные элементы управления */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 1, md: 2 },
                        alignItems: { xs: 'stretch', md: 'center' },
                        justifyContent: 'space-between'
                    }}>
                        {/* Левая группа - кнопка создания и фильтры */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, md: 2 },
                            flex: 1,
                            flexDirection: { xs: 'column', sm: 'row' },
                            flexWrap: 'wrap',
                            alignItems: { xs: 'stretch', sm: 'center' }
                        }}>

                            {/* Кнопка продвинутых фильтров */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FilterIcon />}
                                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                                sx={{
                                    borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    px: 1,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        transform: 'translateY(-1px)',
                                    },
                                    transition: 'all 0.2s ease',
                                    ...(getActiveAdvancedFiltersCount() > 0 && {
                                        bgcolor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                        borderColor: darkMode ? '#42a5f5' : '#1565c0',
                                    })
                                }}
                            >
                                Фильтры
                                {getActiveAdvancedFiltersCount() > 0 && (
                                    <Chip
                                        label={getActiveAdvancedFiltersCount()}
                                        size="small"
                                        sx={{
                                            ml: 0.5,
                                            bgcolor: darkMode ? '#1976d2' : '#1976d2',
                                            color: '#fff',
                                            fontSize: '0.7rem',
                                            height: '16px',
                                            minWidth: '16px'
                                        }}
                                    />
                                )}
                            </Button>
                            {/* Объединенная кнопка экспорта с меню */}
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownloadIcon />}
                                endIcon={<KeyboardArrowDownIcon />}
                                onClick={handleExportMenuOpen}
                                disabled={filteredData.length === 0}
                                sx={{
                                    borderColor: darkMode ? '#1976d2' : '#1976d2',
                                    color: darkMode ? '#1976d2' : '#1976d2',
                                    px: 1,
                                    py: 0.5,
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    borderRadius: 1,
                                    minWidth: 'auto',
                                    '&:hover': {
                                        borderColor: darkMode ? '#1565c0' : '#1565c0',
                                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        transform: 'translateY(-1px)',
                                    },
                                    '&:disabled': {
                                        borderColor: darkMode ? '#666' : '#ccc',
                                        color: darkMode ? '#666' : '#ccc',
                                        transform: 'none',
                                    },
                                    transition: 'all 0.2s ease',
                                }}
                            >
                                Экспорт
                            </Button>
                            {/* Меню выбора формата экспорта */}
                            <Menu
                                anchorEl={exportMenuAnchor}
                                open={Boolean(exportMenuAnchor)}
                                onClose={handleExportMenuClose}
                                anchorOrigin={{
                                    vertical: 'bottom',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                sx={{
                                    '& .MuiPaper-root': {
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        border: darkMode ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.12)',
                                        boxShadow: darkMode ? '0 4px 20px rgba(0, 0, 0, 0.5)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                                    }
                                }}
                            >
                                <MenuItem
                                    onClick={() => handleExportFormatSelect('excel')}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.1)',
                                        }
                                    }}
                                >
                                    <FileDownloadIcon sx={{ mr: 1, color: '#4caf50' }} />
                                    Экспорт в Excel
                                </MenuItem>
                                <MenuItem
                                    onClick={() => handleExportFormatSelect('pdf')}
                                    sx={{
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '&:hover': {
                                            bgcolor: darkMode ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                        }
                                    }}
                                >
                                    <PictureAsPdfIcon sx={{ mr: 1, color: '#f44336' }} />
                                    Экспорт в PDF
                                </MenuItem>
                            </Menu>
                            {/* Фильтр */}
                            {/* <FormControl
                                size="small"
                                sx={{
                                    minWidth: { xs: 'auto', md: '150px' },
                                    flex: { xs: 1, sm: 'none' },
                                    '& .MuiInputLabel-root': {
                                        color: darkMode ? '#bbb' : 'rgba(0,0,0,0.6)',
                                        '&.Mui-focused': {
                                            color: '#1976d2'
                                        }
                                    }
                                }}
                            >
                                <InputLabel>Фильтр</InputLabel>
                                <Select
                                    value={filterMode}
                                    size='small'
                                    onChange={(e) => setFilterMode(e.target.value)}
                                    label="Фильтр"
                                    startAdornment={<FilterIcon sx={{
                                        mr: 1,
                                        color: darkMode ? '#bbb' : 'text.secondary',
                                        fontSize: '18px'
                                    }} />}
                                    sx={{
                                        fontSize: '0.75rem',
                                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: darkMode ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#1976d2'
                                        },
                                        '& .MuiSelect-icon': {
                                            color: darkMode ? '#bbb' : 'rgba(0,0,0,0.54)'
                                        },
                                        '& .MuiInputLabel-root': {
                                            fontSize: '0.7rem'
                                        }
                                    }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                bgcolor: darkMode ? '#2b2b2b' : '#fff',
                                                border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                                                '& .MuiMenuItem-root': {
                                                    fontSize: '0.75rem',
                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                    '&:hover': {
                                                        bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <MenuItem value="all">Все объекты ({data.length})</MenuItem>
                                   
                                </Select>
                            </FormControl> */}

                            {/* Кнопка сброса фильтров */}
                            {/* {(searchQuery || filterMode !== 'all' || selectedKdp.length > 0) && (
                                <Button
                                    size="small"
                                    onClick={() => {
                                        setSearchQuery('');
                                        setFilterMode('all');
                                        setSelectedKdp([]);
                                    }}
                                    sx={{
                                        mb: 1,
                                        color: darkMode ? '#bbb' : '#666',
                                        fontSize: '0.75rem',
                                        textTransform: 'none',
                                        '&:hover': {
                                            color: darkMode ? '#f5f7fa' : '#333',
                                        }
                                    }}
                                >
                                    Сбросить фильтры
                                </Button>
                            )} */}
                        </Box>

                        {/* Правая группа - кнопка редактирования */}
                        <Box sx={{
                            display: 'flex',
                            gap: { xs: 1, md: 2 },
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'stretch', md: 'center' },
                            justifyContent: 'space-between'
                        }}>
                            {/* Индикатор режима редактирования */}
                            {/* {editMode && (
                                <Chip
                                    label="Режим редактирования"
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(25, 118, 210, 0.2)' : 'rgba(25, 118, 210, 0.1)',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(100, 181, 246, 0.3)' : 'rgba(25, 118, 210, 0.3)'}`,
                                    }}
                                />
                            )} */}

                            {/* Индикатор изменений */}
                            {/* {editedRows.length > 0 && (
                                <Chip
                                    label={`${editedRows.length} изменений`}
                                    size="small"
                                    sx={{
                                        bgcolor: darkMode ? 'rgba(255, 152, 0, 0.2)' : 'rgba(255, 152, 0, 0.1)',
                                        color: darkMode ? '#ffb74d' : '#f57c00',
                                        fontSize: '0.7rem',
                                        fontWeight: 600,
                                        border: `1px solid ${darkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.3)'}`,
                                    }}
                                />
                            )} */}






                        </Box>
                    </Box>
                </Box>

                {/* Продвинутые фильтры */}
                {showAdvancedFilters && (
                    <AdvancedFiltersComponent
                        darkMode={darkMode}
                        advancedFilters={advancedFilters}
                        setAdvancedFilters={setAdvancedFilters}
                        stages={stages}
                        onApplyFilters={handleApplyAdvancedFilters}
                        onClearFilters={handleClearAdvancedFilters}
                        addFilter={addFilter}
                        removeFilter={removeFilter}
                        updateFilter={updateFilter}
                        getAvailableFiltersForSelect={getAvailableFiltersForSelect}
                    />
                )}

                {/* Таблица */}
                <Box sx={{
                    display: 'flex',
                    flex: 1,
                    height: '100%',
                    bgcolor: darkMode ? '#1e1e1e' : '#f5f7fa',
                    width: '100%',
                    // overflowX: 'auto',
                }}>
                    {loading ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f5f7fa' : '#333',
                            gap: 2,
                        }}>
                            <CircularProgress size={40} sx={{ color: darkMode ? '#64b5f6' : '#1976d2' }} />
                            <Typography variant="h6">Загрузка данных...</Typography>
                        </Box>
                    ) : error ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#f44336' : '#d32f2f',
                        }}>
                            <Typography variant="h6">Ошибка загрузки: {error}</Typography>
                        </Box>
                    ) : (filteredData.length === 0 && groupedData.groupedData.length === 0) ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                            color: darkMode ? '#bbb' : '#666',
                        }}>
                            <Typography variant="h6">
                                {searchQuery || advancedFilters.length > 0
                                    ? 'По вашему запросу ничего не найдено'
                                    : 'Нет данных для отображения'
                                }
                            </Typography>
                        </Box>
                    ) : (
                        <GroupedTableVirtuoso
                            key={'grouped-table'}
                            data={groupedData.groupedData}
                            overscan={900}
                            groupCounts={groupedData.groupCounts}
                            totalCount={groupedData.groupedData.length}
                            components={VirtuosoTableComponents}
                            fixedHeaderContent={() => fixedHeaderContent(darkMode, sortBy, sortDirection, handleSort, visibleColumns, columnWidths)}
                            groupContent={(index) => groupContent(index, groupedData, darkMode, user, visibleColumns)}
                            itemContent={(index, groupIndex) => {
                                const row = groupedData.groupedData[index];
                                if (!row) {
                                    console.warn('Missing row at index:', index);
                                    return null;
                                }

                                // Для conclusions данные уже находятся в row, не нужно обращаться к row.accounting
                                const cells = rowContent(index, row, {
                                    darkMode,
                                    visibleColumns,
                                    users
                                });

                                // Фильтруем ячейки по видимости столбцов
                                if (row.isPadding) {
                                    return cells; // Для padding элементов уже есть фильтрация внутри rowContent
                                }

                                // Проверяем, что cells существует и имеет правильную структуру
                                if (!cells) {
                                    return null; // Возвращаем null если cells не существует
                                }

                                if (!cells.props || !cells.props.children) {
                                    return cells; // Возвращаем как есть если структура неправильная
                                }

                                const cellsArray = React.Children.toArray(cells.props.children);
                                const visibleCells = cellsArray.filter((cell, cellIndex) => {
                                    return cellIndex < columns.length && visibleColumns[columns[cellIndex].id];
                                });

                                return React.cloneElement(cells, {}, visibleCells);
                            }}
                            style={{
                                backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                                width: '100%',
                                height: '100%',
                                minHeight: '400px',
                            }}

                        />
                    )}
                </Box>
            </Paper >




            {/* Меню настройки видимости столбцов */}
            <Menu
                anchorEl={columnMenuAnchor}
                open={Boolean(columnMenuAnchor)}
                onClose={handleColumnMenuClose}
                PaperProps={{
                    sx: {
                        bgcolor: darkMode ? '#2b2b2b' : '#fff',
                        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0,0,0,0.12)',
                        minWidth: 250,
                        maxHeight: 400,
                        '& .MuiMenuItem-root': {
                            fontSize: '0.875rem',
                            color: darkMode ? '#f5f7fa' : '#333',
                            padding: '0',
                            pr: 1,
                            borderRadius: '6px',
                            '&:hover': {
                                bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)'
                            }
                        }
                    }
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                {/* Заголовок меню */}
                <MenuItem disabled sx={{ display: 'flex', justifyContent: 'center', margin: '0 !important', padding: '0 !important', borderRadius: '0 !important' }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: darkMode ? '#f5f7fa' : '#333' }}>
                        Видимость столбцов
                    </Typography>
                </MenuItem>

                <Divider sx={{
                    bgcolor: darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                    margin: '4px 0'
                }} />

                {/* Список столбцов с чекбоксами */}
                {columns.map((column) => (
                    <MenuItem
                        key={column.id}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Checkbox
                            checked={visibleColumns[column.id] || false}
                            onChange={(e) => handleColumnVisibilityChange(column.id, e.target.checked)}
                            size="small"
                            sx={{
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                '&.Mui-checked': {
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                },
                                mr: 0
                            }}
                        />
                        <ListItemText
                            primary={column.label}
                            primaryTypographyProps={{
                                fontSize: '0.875rem',
                                color: darkMode ? '#f5f7fa' : '#333'
                            }}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </Box >
    );
};

export default FieldWorksView;
